/** IXD 셀 포커싱 전용 훅 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { IxdChunk } from "@/apis/compare/route";
import {
  focusCell,
  clearFocusedCell,
  planFocusScroll,
  applyFocusScroll,
  IXD_SECTION_SELECTOR,
} from "@/utils/ixd-dsd-compare/focus-utils";
import { IxdPreviewRendererRef } from "@/components/ixd-preview";
import { MatchNavigation } from "@/app/ixd-dsd-compare/types";
import { periodOfCellId } from "@/utils/ixd-dsd-compare/cell-id-selector";
import { ATTR } from "@/utils/dom-attrs";
import * as Sentry from "@sentry/react";

interface UseIxdFocusParams {
  ixdRendererRef?: React.RefObject<IxdPreviewRendererRef>;
  currentIxdChunks?: IxdChunk[];
  onActiveIxdChunkChange?: (chunk: IxdChunk) => void;
  activeIxdChunkId?: string;
}

/**
 * 같은 cell_id 가 여러 DL 에 실릴 수 있어(같은 fact 를 두 DL 이 태깅), cell_id 검색만
 * 으로는 어느 DL 의 셀을 포커싱할지 정해지지 않는다. 호출자가 넘긴 DL 안의 후보를 먼저
 * 고른다 — 매칭 클릭에선 정본 셀이 속한 DL, 추론값에선 클릭한 DSD 셀의 배정이다.
 *
 * 그 DL 에 후보가 없으면 기본은 전체를 그대로 돌려준다 — 매칭 셀은 IXD 가 실제로
 * 그 값을 들고 있으므로 다른 DL 의 셀을 보여주는 것이 맞다(서버
 * SelectProperCellIdStage 의 폴백).
 *
 * ``strict`` 는 그 폴백을 끈다. 추론값(should_tag·inferredCellId)은 "배정된 DL 안에서
 * 여기쯤" 이라는 제안이라, 같은 cell_id 를 쓰는 **다른 DL 의 값**으로 데려가면 그 값이
 * 이 DSD 셀의 대응인 것처럼 보인다. 후보가 없으면 빈 배열을 돌려 호출자가 그 DL 까지만
 * 안내하게 한다.
 */
export function preferCellsInDlView(
  cells: HTMLElement[],
  dlViewId?: string | null,
  options?: { strict?: boolean },
): HTMLElement[] {
  if (!dlViewId) return cells;
  const scoped = cells.filter(
    (cell) =>
      cell.closest("[data-dl-id]")?.getAttribute("data-dl-id") === dlViewId,
  );
  if (scoped.length > 0) return scoped;
  return options?.strict ? [] : cells;
}

/**
 * 같은 fact 가 당기·전기 양쪽 셀로 찾힐 수 있어, cell_id 검색만으로는 어느 기간의
 * 셀을 포커싱할지 정해지지 않는다. period 가 빈 정본(`:X::`)으로 매칭되면
 * `periodAgnosticCellIdSelector` 가 접미사 일치로 두 기간을 모두 찾고, 당기·전기가
 * 한 DL 안에 있으면 `preferCellsInDlView` 로도 안 갈린다. 호출자가 기간을 알면 그
 * 기간의 후보를 먼저 고른다.
 *
 * 그 기간에 후보가 없으면 전체를 그대로 돌려준다 — `preferCellsInDlView` 와 같은
 * 이유로, 좁히다 0 건이 되면 포커싱이 통째로 사라진다.
 */
export function preferCellsInPeriod(
  cells: HTMLElement[],
  period?: string | null,
): HTMLElement[] {
  if (!period) return cells;
  const scoped = cells.filter(
    (cell) => periodOfCellId(cell.getAttribute(ATTR.cellId) ?? "") === period,
  );
  return scoped.length > 0 ? scoped : cells;
}

export const useIxdFocus = ({
  ixdRendererRef,
  currentIxdChunks,
  onActiveIxdChunkChange,
  activeIxdChunkId,
}: UseIxdFocusParams) => {
  const focusedCellRef = useRef<HTMLElement | null>(null);
  /**
   * 스크롤 기준선용 "직전에 포커싱했던 셀". `focusedCellRef` 와 수명이 다르다 —
   * 그쪽은 강조 표시의 소유자라 `clearFocus()`(IXD 셀 직접 클릭·네비 팝오버 닫기)로
   * 비워지는데, 그때 "직전에 보던 표"까지 잊으면 같은 표의 셀을 다시 클릭했을 때
   * "표가 바뀌었다" 로 판정해 표 머리로 되돌아간다.
   */
  const scrollAnchorCellRef = useRef<HTMLElement | null>(null);
  const activeIxdChunkIdRef = useRef(activeIxdChunkId);

  useEffect(() => {
    activeIxdChunkIdRef.current = activeIxdChunkId;
  }, [activeIxdChunkId]);

  /**
   * 포커싱한 셀이 속한 IXD chunk 를 상위에 알린다(섹션 탭 커서 갱신용).
   * 모든 chunk 가 한 스크롤에 함께 그려지므로 표시 대상을 바꾸지는 않는다.
   */
  const notifyActiveChunkFromCell = useCallback(
    (cell: HTMLElement): void => {
      if (!onActiveIxdChunkChange || !currentIxdChunks?.length) return;
      const chunkId = cell
        .closest("[data-chunk-id]")
        ?.getAttribute("data-chunk-id");
      if (!chunkId || chunkId === activeIxdChunkIdRef.current) return;
      const chunk = currentIxdChunks.find((c) => c.id === chunkId);
      if (!chunk) return;
      activeIxdChunkIdRef.current = chunk.id;
      onActiveIxdChunkChange(chunk);
    },
    [currentIxdChunks, onActiveIxdChunkChange],
  );

  /**
   * 이전 포커스를 정리하고 새 셀에 포커스를 적용한다.
   *
   * 강조를 먼저 하고 스크롤은 뒤에 건다 — smooth 스크롤은 비동기라 강조까지 미루면
   * 클릭 반응이 늦게 보인다. "표가 바뀌었나" 판정의 기준은 `scrollAnchorCellRef` 다 —
   * 강조 소유자(`focusedCellRef`)를 쓰면 외부 `clearFocus()` 뒤 첫 클릭에서 판정이
   * 항상 참이 되어 같은 표인데도 표 머리로 올라간다.
   */
  const applyFocus = useCallback(
    ({ cell, bgColor }: { cell: HTMLElement; bgColor?: string }) => {
      const prevCell = scrollAnchorCellRef.current;
      const container = ixdRendererRef?.current?.getScrollContainer();

      clearFocusedCell(focusedCellRef);
      ixdRendererRef?.current?.clearFocus();
      notifyActiveChunkFromCell(cell);
      focusCell({ cell, focusedCellRef, bgColor });
      scrollAnchorCellRef.current = cell;

      if (container) {
        applyFocusScroll(
          planFocusScroll({
            cell,
            prevCell,
            container,
            sectionSelector: IXD_SECTION_SELECTOR,
          }),
        );
      }
    },
    [ixdRendererRef, notifyActiveChunkFromCell],
  );

  /**
   * 셀 대신 DL 표까지만 데려간다. 배정된 DL 에 대응 셀이 없을 때 쓰며, 이전 포커스는
   * 지운다 — 남겨두면 무관한 셀 강조가 이 클릭의 결과처럼 보인다.
   */
  const focusDlView = useCallback(
    (dlViewId: string) => {
      clearFocusedCell(focusedCellRef);
      ixdRendererRef?.current?.clearFocus();
      const section = ixdRendererRef?.current
        ?.getScrollContainer()
        ?.querySelector<HTMLElement>(`[data-dl-id="${CSS.escape(dlViewId)}"]`);
      if (!section) return;
      notifyActiveChunkFromCell(section);
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [ixdRendererRef, notifyActiveChunkFromCell],
  );

  /**
   * IXD 셀 ID 목록으로 직접 포커싱 (primary path).
   * 값 기반 검색 없이 ID로 DOM 요소를 직접 찾아 포커싱.
   */
  const focusByCellIds = useCallback(
    ({
      cellIds,
      identityKeysByCellId,
      value,
      alwaysReturnNav,
      mostProperCellId,
      dlViewId,
      period,
      restrictToDlView,
      bgColor,
    }: {
      cellIds: string[];
      /**
       * cellId → 매칭 fact identity key 목록. 등록된 cellId 는 동일 cellId 의
       * 다른 축 셀을 걸러내고, 등록 안 된 cellId 는 기존처럼 필터 없이 검색한다.
       */
      identityKeysByCellId?: Map<string, string[]>;
      value?: string;
      alwaysReturnNav?: boolean;
      mostProperCellId?: string | null;
      /**
       * 포커싱 대상을 좁힐 DL. 매칭 클릭은 **정본 셀이 실제로 속한** DL 이고(백엔드
       * `mostProperCell.dlViewId`), 정본이 없거나 추론값이면 클릭한 DSD 셀의 배정이다.
       */
      dlViewId?: string | null;
      /**
       * 포커싱 대상을 좁힐 기간. 정본이 기간을 담고 있으면 그 값, 아니면 클릭한 DSD
       * 셀의 배정이다 — 같은 cell_id 가 당기·전기에 걸릴 때 이 기간을 우선한다.
       */
      period?: string | null;
      /**
       * 추론값처럼 배정 DL 밖으로 나가면 안 되는 포커싱. 그 DL 에 대응 셀이 없으면
       * 다른 DL 의 같은 cell_id 로 가지 않고 그 DL 까지만 스크롤한다.
       */
      restrictToDlView?: boolean;
      bgColor?: string;
    }): MatchNavigation | null => {
      if (!cellIds?.length || !ixdRendererRef?.current) return null;

      const matchingCells = ixdRendererRef.current.searchByCellIds(
        cellIds,
        identityKeysByCellId,
      );
      if (
        restrictToDlView &&
        dlViewId &&
        preferCellsInDlView(matchingCells, dlViewId, { strict: true })
          .length === 0
      ) {
        focusDlView(dlViewId);
        return null;
      }
      // 매칭 셀을 못 찾았으면 새 포커스를 못 잡으므로 이전 포커스를 정리한다.
      // (정리하지 않으면 직전에 포커싱했던 무관한 셀의 강조가 그대로 남는다.)
      if (matchingCells.length === 0) {
        clearFocusedCell(focusedCellRef);
        ixdRendererRef.current.clearFocus();
        return null;
      }

      // 매칭 클릭 경로의 첫 포커스는 **mostProperCellId 하나로** 정해진다. 백엔드가
      // 매칭이 있으면 이 값을 항상 채우고(`ResolvedItem.focus_cell_id`), 셀 색 판정도
      // 같은 셀만 보므로 색과 포커스가 어긋나지 않는다. 인자가 optional 인 것은 이
      // 훅을 매칭 클릭과 추론값 포커싱(`focusInferredCell` — mostProper 개념이 없다)이
      // 함께 쓰기 때문이고, 계약상 "없을 수 있는" 경로는 그쪽뿐이다.
      const properCells = mostProperCellId
        ? ixdRendererRef.current.searchByCellIds(
            [mostProperCellId],
            identityKeysByCellId,
          )
        : [];
      // 그 셀이 화면에 없거나 identity 필터(축 충돌 구분)에 걸리면 0 건이 된다. 이때
      // 매칭 목록으로 물러서면 **색 판정에 쓰인 셀과 다른 셀**을 포커싱하게 되므로,
      // 재현이 어려운 이 경로를 조용히 넘기지 않고 남긴다.
      if (mostProperCellId && properCells.length === 0) {
        Sentry.captureMessage(
          "[대사] mostProper 셀을 못 찾아 매칭 목록으로 폴백",
          {
            level: "warning",
            extra: {
              mostProperCellId,
              cellIds,
              hasIdentityFilter:
                identityKeysByCellId?.has(mostProperCellId) ?? false,
              matchingCellCount: matchingCells.length,
              dlViewId,
              period,
            },
          },
        );
      }
      // 같은 cell_id 가 여러 DL·기간에 걸리면 배정된 쪽을 고른다 — 어느 물리 셀인지
      // 는 cell_id 만으로 안 갈린다.
      const firstCell = preferCellsInPeriod(
        preferCellsInDlView(
          properCells.length ? properCells : matchingCells,
          dlViewId,
        ),
        period,
      )[0];
      applyFocus({ cell: firstCell, bgColor });
      ixdRendererRef.current.activateSameValueForCell(firstCell);

      // navigation 목록은 좁히지도, 재정렬하지도 않는다 — 사용자가 다른 DL 의 매칭도
      // 문서 순서대로 순회할 수 있어야 한다(팝오버 목록과 prev/next 가 이 배열 순서를
      // 그대로 따른다). 대신 currentIndex 를 실제 포커싱한 셀에 맞춘다.
      // (mostProperCellId 가 cellIds 밖이라 목록에 없으면 맨 앞에 얹는다.)
      const focusedIndex = matchingCells.indexOf(firstCell);
      const orderedCells =
        focusedIndex >= 0 ? matchingCells : [firstCell, ...matchingCells];
      const currentIndex = focusedIndex >= 0 ? focusedIndex : 0;

      if (orderedCells.length === 1 && !alwaysReturnNav) return null;

      return {
        value: value || "",
        matchingCells: orderedCells,
        currentIndex,
      };
    },
    [ixdRendererRef, applyFocus, focusDlView],
  );

  /**
   * DSD 헤더 태그(lineItem/period/member)에 대응하는 IXD 헤더(th)로 포커싱.
   * 매칭 헤더가 없으면 false.
   */
  const focusHeaderByTag = useCallback(
    (tag: {
      lineItem?: string | null;
      period?: string | null;
      member?: string | null;
    }): boolean => {
      const els = ixdRendererRef?.current?.searchHeaderByTag(tag) ?? [];
      // 대응 헤더를 못 찾았으면 이전 포커스를 정리한다(stale 강조 방지).
      if (els.length === 0) {
        clearFocusedCell(focusedCellRef);
        ixdRendererRef?.current?.clearFocus();
        return false;
      }
      applyFocus({ cell: els[0] });
      return true;
    },
    [ixdRendererRef, applyFocus],
  );

  /**
   * 매칭된 셀 목록에서 특정 인덱스의 셀에 포커스.
   * navigation prev/next에서 사용.
   */
  const focusByIndex = useCallback(
    (cells: HTMLElement[], index: number) => {
      const cell = cells[index];
      if (!cell) return;
      applyFocus({ cell });
    },
    [applyFocus],
  );

  const clear = useCallback(() => {
    if (ixdRendererRef?.current) {
      ixdRendererRef.current.clearFocus();
    }
    clearFocusedCell(focusedCellRef);
  }, [ixdRendererRef]);

  return useMemo(
    () => ({
      focusByCellIds,
      focusHeaderByTag,
      focusByIndex,
      clearFocus: clear,
    }),
    [focusByCellIds, focusHeaderByTag, focusByIndex, clear],
  );
};
